import React, { useState, useEffect } from 'react';
import { WindowFrame } from './WindowFrame';
import { BaseNodeWrapper } from './BaseNodeWrapper';
import { NodeFrameWrapper } from './NodeFrameWrapper';
import { logger } from '../../utils/logger';

interface ImageNodeProps {
  id: string;
  type?: 'image' | 'video';
  src: string;
  title?: string;
  frameStyle: 'browser' | 'none';
  isEditing?: boolean;
  workInProgress?: boolean;
  wipBannerOffset?: number;
  borderRadius?: number;
  onTitleChange?: (title: string) => void;
  onUpdate?: (updates: { wipBannerOffset?: number; borderRadius?: number }) => void;
}

export const ImageNode: React.FC<ImageNodeProps> = ({
  type = 'image',
  src,
  title,
  frameStyle,
  isEditing,
  workInProgress = false,
  wipBannerOffset = 0,
  borderRadius = 8,
  onTitleChange,
  onUpdate,
}) => {
  const [imageError, setImageError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    logger.info('media', `Rendering ${type} node`, { src, title });
    setImageError(false);
    setIsLoading(true);
    setRetryCount(0);
  }, [src, type]);

  useEffect(() => {
    if (imageError && retryCount < 3) {
      const timeout = setTimeout(() => {
        setRetryCount(prev => prev + 1);
        setImageError(false);
        setIsLoading(true);
      }, 1000 * (retryCount + 1));
      return () => clearTimeout(timeout);
    }
  }, [imageError, retryCount]);

  const imageContent = (
    <div className="w-full h-full relative" style={{ lineHeight: 0 }}>
      {isLoading && !imageError && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
          <div className="text-gray-400">Loading...</div>
        </div>
      )}
      {imageError && retryCount >= 3 && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
          <div className="text-gray-400">Failed to load {type}</div>
        </div>
      )}
      {type === 'video' ? (
        <video
          key={retryCount}
          src={src}
          className="w-full h-full object-cover block"
          style={{ display: isLoading || (imageError && retryCount >= 3) ? 'none' : 'block' }}
          autoPlay
          loop
          muted
          playsInline
          onLoadedData={() => {
            logger.debug('media', 'Video loaded successfully', { src });
            setIsLoading(false);
          }}
          onError={(e) => {
            logger.error('media', 'Video failed to load', { src, error: e });
            setIsLoading(false);
            setImageError(true);
          }}
        />
      ) : (
        <img
          key={retryCount}
          src={src}
          alt="Slide content"
          className="w-full h-full object-cover block"
          style={{ display: isLoading || (imageError && retryCount >= 3) ? 'none' : 'block' }}
          onLoad={() => {
            logger.debug('media', 'Image loaded successfully', { src });
            setIsLoading(false);
          }}
          onError={(e) => {
            logger.error('media', 'Image failed to load', { src, error: e });
            setIsLoading(false);
            setImageError(true);
          }}
        />
      )}
    </div>
  );

  if (frameStyle === 'none') {
    return (
      <BaseNodeWrapper
        workInProgress={workInProgress}
        wipBannerOffset={wipBannerOffset}
        isEditing={isEditing}
        onUpdate={onUpdate}
      >
        <NodeFrameWrapper borderRadius={borderRadius}>
          {imageContent}
        </NodeFrameWrapper>
      </BaseNodeWrapper>
    );
  }

  return (
    <BaseNodeWrapper
      workInProgress={workInProgress}
      wipBannerOffset={wipBannerOffset}
      isEditing={isEditing}
      onUpdate={onUpdate}
    >
      <WindowFrame
        title={title}
        onTitleChange={onTitleChange}
        isEditing={isEditing}
        borderRadius={borderRadius}
      >
        <div className="w-full h-full overflow-hidden" style={{ lineHeight: 0 }}>
          {imageContent}
        </div>
      </WindowFrame>
    </BaseNodeWrapper>
  );
};
